import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getCustomerToken as _gc, setCustomerSession, clearCustomerSession } from "../../../lib/customer";
import { Loader2, LogOut, User, Landmark, Users, FileText, Bell, ShieldCheck, LayoutDashboard,
  Plus, Download, Printer, Search, ChevronLeft, ChevronRight, Camera, KeyRound, X, Heart } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../../../lib/api";
import { printStatement, stmtQS } from "./ReOrgPanel";

const tKey = (slug) => `zylo_remember_${slug}`;
const iKey = (slug) => `zylo_remember_${slug}_info`;
const getTok = (slug) => localStorage.getItem(tKey(slug));
const getInfo = (slug) => { try { return JSON.parse(localStorage.getItem(iKey(slug))); } catch { return null; } };
const setSess = (slug, token, member) => { localStorage.setItem(tKey(slug), token); localStorage.setItem(iKey(slug), JSON.stringify(member)); };
const clearSess = (slug) => { localStorage.removeItem(tKey(slug)); localStorage.removeItem(iKey(slug)); };

const inr2 = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const METHODS = ["PhonePe", "Google Pay", "UPI", "Bank Transfer", "NEFT", "IMPS", "Manual / Cash", "Other"];
const PAY_STATUS = ["PENDING", "VERIFIED", "REJECTED", "REVERSED"];
const STATUS_STYLE = { PENDING: "#d97706", VERIFIED: "#16a34a", REJECTED: "#dc2626", REVERSED: "#7c3aed" };

const PROFILE_ROWS = [
  ["full_name", "Full Name"], ["joining_date", "Joining Date"], ["email", "Email Address"],
  ["whatsapp", "WhatsApp Number"], ["mobile", "Mobile Number"], ["referral_id", "Referral ID"],
  ["rank", "Rank"], ["client_id", "Client ID"], ["district", "District"],
  ["investment_amount", "Investment Amount"], ["closing_date", "Closing Date"],
];

function OrgLogin({ slug, onLogin }) {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inp = "w-full h-12 px-4 rounded-xl text-sm font-semibold bg-transparent border outline-none focus:ring-2";
  const bc = { borderColor: "color-mix(in srgb, var(--re-t) 15%, transparent)" };
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const url = mode === "register" ? "org/register" : "org/login";
      const payload = mode === "register" ? { name: f.name, email: f.email, password: f.password, mobile: f.phone } : { email: f.email, password: f.password };
      const { data } = await api.post(`/re/public/${slug}/${url}`, payload);
      setSess(slug, data.token, data.member);
      setCustomerSession(slug, data.token, { name: data.member.full_name, email: data.member.email, phone: data.member.mobile || "" });
      onLogin(data.member);
      toast.success(`Welcome${mode === "register" ? "" : " back"}, ${data.member.full_name}!`);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-14" data-testid="re-org-login">
      <div className="re-card p-8">
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "color-mix(in srgb, var(--re-p) 12%, transparent)", color: "var(--re-p)" }}>
          <Users className="h-6 w-6" />
        </div>
        <h1 className="re-h font-extrabold text-2xl tracking-tight text-center">{mode === "login" ? "Client Login" : "Create Account"}</h1>
        <p className="text-xs opacity-60 text-center mt-1.5">{mode === "login" ? "Access your dashboard, statement & saved properties" : "Register to get your Client ID and dashboard"}</p>
        {err && <p className="mt-4 text-xs font-bold text-center px-4 py-2.5 rounded-xl" style={{ background: "rgba(220,38,38,.08)", color: "#dc2626" }} data-testid="re-org-login-err">{err}</p>}
        <form onSubmit={submit} className="space-y-3.5 mt-6">
          {mode === "register" && (
            <>
              <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full Name *" data-testid="ra-name" className={inp} style={bc} />
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Mobile Number" data-testid="ra-phone" className={inp} style={bc} />
            </>
          )}
          <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email *" data-testid="ra-email" className={inp} style={bc} />
          <input type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password * (min 6)" data-testid="ra-password" className={inp} style={bc} />
          <button type="submit" disabled={busy} className="re-btn w-full h-12 text-sm" data-testid="ra-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : mode === "login" ? "Login" : "Create Account"}
          </button>
        </form>
        <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="w-full text-center text-xs font-bold mt-4 opacity-70 hover:opacity-100" data-testid="ra-switch">
          {mode === "login" ? "New here? Create an account" : "Already registered? Login"}
        </button>
        <p className="text-[11px] opacity-50 text-center mt-4">Director / Manager accounts: use the <Link to="/login" className="underline font-bold">common MILTROO login</Link>.</p>
      </div>
    </div>
  );
}

export default function ReMember({ slug, data, go }) {
  const [member, setMember] = useState(() => getInfo(slug));
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [clients, setClients] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [sf, setSf] = useState({ mode: "month", month: new Date().getMonth() + 1, year: new Date().getFullYear(), fy: `${new Date().getFullYear()}-${String(new Date().getFullYear() + 1).slice(2)}`, from: "", to: "", method: "", status: "" });
  const [cq, setCq] = useState("");
  const [page, setPage] = useState(0);
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const tok = getTok(slug);
  const H = useMemo(() => ({ Authorization: `Bearer ${tok}` }), [tok]);

  const loadMe = () => api.get("/re/org/me", { headers: H }).then((r) => { setMe(r.data); setMember(r.data.member); setSess(slug, tok, r.data.member); }).catch((e) => { if (e.response?.status === 401) { clearSess(slug); setMember(null); } });
  const loadDash = () => api.get("/re/org/dashboard", { headers: H }).then((r) => setDash(r.data)).catch(() => {});
  const loadClients = (query = cq) => api.get(`/re/org/clients${query ? `?q=${encodeURIComponent(query)}` : ""}`, { headers: H }).then((r) => { setClients(r.data.clients); setPage(0); }).catch(() => setClients([]));
  const loadNotifs = () => api.get("/re/org/notifications", { headers: H }).then((r) => setNotifs(r.data)).catch(() => setNotifs({ notifications: [] }));
  const loadStmt = (filters = sf) => api.get(`/re/org/statement?${stmtQS(filters)}`, { headers: H }).then((r) => setStmt(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    if (!member || !tok) return;
    loadMe(); loadDash(); loadNotifs();
  }, []);
  useEffect(() => {
    if (!member) return;
    if (tab === "clients") loadClients();
    if (tab === "statement") loadStmt();
    if (tab === "notifications") loadNotifs();
  }, [tab]);

  if (!member || !tok) return <OrgLogin slug={slug} onLogin={setMember} />;

  const perms = member.permissions || [];
  const can = (p) => perms.includes(p);
  const isStaff = member.role !== "CLIENT";

  const NAV = [
    ["dashboard", "Dashboard", LayoutDashboard, true],
    ["profile", "My Profile", User, true],
    ["clients", "My Clients", Users, can("clients_view")],
    ["statement", "My Statement", FileText, can("own_statement") || can("statements")],
    ["notifications", "Notifications", Bell, true],
    ["security", "Security Settings", ShieldCheck, true],
    ["favourites", "Favourites & Enquiries", Heart, true],
  ].filter(([, , , ok]) => ok);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); loadMe(); loadDash(); if (tab === "clients") loadClients(); if (tab === "statement") loadStmt(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const PAGE = 10;
  const paged = (clients || []).slice(page * PAGE, page * PAGE + PAGE);
  const pages = Math.max(1, Math.ceil((clients || []).length / PAGE));

  const stBadge = (s) => <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full text-white" style={{ background: STATUS_STYLE[s] || "#666" }}>{s}</span>;
  const years = []; const cy = new Date().getFullYear();
  for (let y = cy - 4; y <= cy + 1; y++) years.push(y);

  const Card = ({ label, value, sub, testid, accent }) => (
    <div className="re-card p-5" data-testid={testid}>
      <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-50">{label}</p>
      <p className="re-h font-extrabold text-2xl mt-1.5" style={accent ? { color: "var(--re-p)" } : undefined}>{value}</p>
      {sub && <p className="text-[11px] opacity-50 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="re-member-portal">
      <div className="grid lg:grid-cols-[250px_1fr] gap-6 items-start">
        {/* SIDEBAR */}
        <aside className="re-card p-5 lg:sticky lg:top-24" data-testid="re-member-sidebar">
          <div className="flex lg:flex-col items-center lg:items-start gap-4">
            {member.photo_url
              ? <img src={fileUrl(member.photo_url)} alt="" className="h-14 w-14 rounded-full object-cover border" style={{ borderColor: "color-mix(in srgb, var(--re-t) 15%, transparent)" }} />
              : <div className="h-14 w-14 rounded-full flex items-center justify-center text-white font-extrabold text-xl shrink-0" style={{ background: "var(--re-p)" }}>{(member.full_name || "M")[0]}</div>}
            <div className="min-w-0">
              <p className="font-extrabold text-sm truncate">{member.full_name}</p>
              <p className="text-[11px] font-bold mt-0.5" style={{ color: "var(--re-p)" }}>{member.role_label}</p>
              {member.client_id && <p className="text-[10px] opacity-50 font-mono mt-0.5">{member.client_id}</p>}
            </div>
          </div>
          <nav className="flex lg:flex-col gap-1.5 mt-5 overflow-x-auto pb-1">
            {NAV.map(([k, l, I]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`rm-nav-${k}`}
                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-colors"
                style={tab === k ? { background: "var(--re-p)", color: "#fff" } : { color: "color-mix(in srgb, var(--re-t) 70%, transparent)" }}>
                <I className="h-4 w-4 shrink-0" />{l}
                {k === "notifications" && notifs?.unread > 0 && <span className="ml-auto text-[9px] font-extrabold px-1.5 py-0.5 rounded-full text-white" style={{ background: "#dc2626" }}>{notifs.unread}</span>}
              </button>
            ))}
            <button onClick={() => {
              clearSess(slug); clearCustomerSession(slug); toast.success("Logged out");
              const root = window.location.pathname.replace(/\/(org|account).*$/, "") || "/";
              window.location.href = `${root === "/" ? "" : root}/account`;
            }} data-testid="rm-logout"
              className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap" style={{ color: "#dc2626" }}>
              <LogOut className="h-4 w-4 shrink-0" />Logout
            </button>
          </nav>
        </aside>

        {/* CONTENT */}
        <div className="min-w-0">
          {tab === "dashboard" && (
            <div className="space-y-6" data-testid="rm-dashboard">
              <div>
                <h1 className="re-h font-extrabold text-2xl sm:text-3xl tracking-tight">Welcome, {member.full_name?.split(" ")[0]}</h1>
                <p className="text-xs opacity-55 mt-1">{member.role_label}{member.manager ? ` · Manager: ${member.manager.full_name}` : ""}</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card label="Investment Amount" value={inr2(me?.member?.investment_amount)} testid="rm-investment" />
                <Card label="Verified Paid" value={inr2(me?.member?.verified_paid)} testid="rm-paid" accent />
                <Card label="Remaining Balance" value={inr2(me?.member?.balance)} testid="rm-balance" />
                <Card label="Pending Verifications" value={dash?.pending_payments ?? "—"} testid="rm-pending" />
                {dash?.clients && <>
                  <Card label="Assigned Clients" value={dash.clients.total} testid="rm-clients" />
                  <Card label="New This Month" value={dash.clients.new_this_month} testid="rm-new-clients" />
                </>}
              </div>
              <div className="re-card p-6">
                <p className="font-extrabold text-sm mb-3">Quick Info</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  {[["Client ID", member.client_id], ["Referral ID", member.referral_id], ["Rank", member.rank],
                    ["Joining Date", member.joining_date], ["District", member.district], ["Status", member.status]].filter(([, v]) => v).map(([l, v]) => (
                    <div key={l}><p className="text-[10px] font-bold uppercase tracking-wider opacity-45">{l}</p><p className="font-bold mt-0.5 capitalize">{v}</p></div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "profile" && me && <ProfileSections me={me} H={H} reload={loadMe} />}

          {tab === "clients" && can("clients_view") && (
            <div className="space-y-5" data-testid="rm-clients-page">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="re-h font-extrabold text-xl flex-1">My Clients</h2>
                {can("clients_add") && (
                  <button className="re-btn h-10 px-5 text-xs flex items-center gap-1.5" data-testid="rm-add-client"
                    onClick={() => setDlg({ mode: "addClient", form: { full_name: "", email: "", password: "", mobile: "", district: "", investment_amount: "", joining_date: "" } })}>
                    <Plus className="h-3.5 w-3.5" /> Add Client</button>
                )}
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 opacity-40" />
                  <input value={cq} onChange={(e) => setCq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadClients()} placeholder="Search name, Client ID, mobile…" data-testid="rm-client-search"
                    className="w-full h-11 pl-10 pr-4 rounded-xl text-sm font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
                </div>
                <button onClick={() => loadClients()} className="re-btn h-11 px-5 text-xs" data-testid="rm-client-search-btn">Search</button>
              </div>
              {!clients ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
                : clients.length === 0 ? <p className="text-sm opacity-50 py-8 text-center">No clients assigned to you yet.</p> : (
                <>
                  <div className="re-card overflow-x-auto">
                    <table className="w-full text-sm min-w-[760px]" data-testid="rm-clients-table">
                      <thead><tr className="text-left text-[10px] uppercase tracking-wider opacity-50 border-b" style={{ borderColor: "color-mix(in srgb, var(--re-t) 10%, transparent)" }}>
                        {["S.No", "Client ID", "Client Name", "Mobile Number", "Joining Date", "Verified Paid", "Balance", "Actions"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {paged.map((c, i) => (
                          <tr key={c.id} className="border-b last:border-0" style={{ borderColor: "color-mix(in srgb, var(--re-t) 8%, transparent)" }} data-testid={`rm-client-${c.id}`}>
                            <td className="px-4 py-3">{page * PAGE + i + 1}</td>
                            <td className="px-4 py-3 font-mono text-xs font-bold">{c.client_id}</td>
                            <td className="px-4 py-3 font-bold">{c.full_name}</td>
                            <td className="px-4 py-3 text-xs">{c.mobile || "—"}</td>
                            <td className="px-4 py-3 text-xs">{c.joining_date || "—"}</td>
                            <td className="px-4 py-3 font-bold text-xs">{inr2(c.verified_paid)}</td>
                            <td className="px-4 py-3 font-bold text-xs" style={{ color: c.balance > 0 ? "#d97706" : undefined }}>{inr2(c.balance)}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1.5">
                                <button onClick={() => setDlg({ mode: "client", id: c.id })} className="re-chip h-8 px-3.5 text-[11px] font-bold" data-testid={`rm-view-${c.id}`}>View</button>
                                {can("payments_add") && (
                                  <button onClick={() => setDlg({ mode: "payment", member_id: c.id, name: c.full_name, form: { amount: "", direction: "received", payment_date: new Date().toISOString().slice(0, 10), payment_time: new Date().toTimeString().slice(0, 5), method: "UPI", txn_ref: "", remarks: "" } })}
                                    className="re-chip h-8 px-3.5 text-[11px] font-bold" style={{ color: "var(--re-p)" }} data-testid={`rm-pay-${c.id}`}>+ Payment</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pages > 1 && (
                    <div className="flex items-center justify-center gap-3">
                      <button disabled={page === 0} onClick={() => setPage(page - 1)} className="re-chip h-9 w-9 flex items-center justify-center disabled:opacity-40" data-testid="rm-page-prev"><ChevronLeft className="h-4 w-4" /></button>
                      <span className="text-xs font-bold">{page + 1} / {pages}</span>
                      <button disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="re-chip h-9 w-9 flex items-center justify-center disabled:opacity-40" data-testid="rm-page-next"><ChevronRight className="h-4 w-4" /></button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "statement" && (
            <div className="space-y-5" data-testid="rm-statement">
              <h2 className="re-h font-extrabold text-xl">Payment Statement</h2>
              <div className="re-card p-4 flex flex-wrap gap-2.5 items-end">
                <select value={sf.mode} onChange={(e) => setSf({ ...sf, mode: e.target.value })} data-testid="rm-sf-mode"
                  className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                  <option value="all">All Time</option><option value="month">Month</option><option value="fy">Financial Year</option><option value="custom">Custom Range</option>
                </select>
                {sf.mode === "month" && <>
                  <select value={sf.month} onChange={(e) => setSf({ ...sf, month: Number(e.target.value) })} data-testid="rm-sf-month"
                    className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                    {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={sf.year} onChange={(e) => setSf({ ...sf, year: Number(e.target.value) })} data-testid="rm-sf-year"
                    className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </>}
                {sf.mode === "fy" && (
                  <select value={sf.fy} onChange={(e) => setSf({ ...sf, fy: e.target.value })} data-testid="rm-sf-fy"
                    className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                    {years.map((y) => <option key={y} value={`${y}-${String(y + 1).slice(2)}`}>FY {y}-{String(y + 1).slice(2)}</option>)}
                  </select>)}
                {sf.mode === "custom" && <>
                  <input type="date" value={sf.from} onChange={(e) => setSf({ ...sf, from: e.target.value })} data-testid="rm-sf-from"
                    className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
                  <input type="date" value={sf.to} onChange={(e) => setSf({ ...sf, to: e.target.value })} data-testid="rm-sf-to"
                    className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
                </>}
                <select value={sf.method} onChange={(e) => setSf({ ...sf, method: e.target.value })} data-testid="rm-sf-method"
                  className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                  <option value="">All Methods</option>{METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={sf.status} onChange={(e) => setSf({ ...sf, status: e.target.value })} data-testid="rm-sf-status"
                  className="h-10 px-3 rounded-xl text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                  <option value="">All Status</option>{PAY_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => loadStmt()} className="re-btn h-10 px-5 text-xs" data-testid="rm-sf-apply">Apply</button>
                {stmt && <>
                  <button onClick={() => printStatement(data.website?.name || "Real Estate", stmt.summary, stmt.rows)} className="re-chip h-10 px-4 text-xs font-bold inline-flex items-center gap-1.5" data-testid="rm-stmt-print"><Printer className="h-3.5 w-3.5" /> Print</button>
                  <button onClick={async () => {
                    try { const r = await api.get(`/re/org/statement.pdf?${stmtQS(sf)}`, { headers: H, responseType: "blob" }); window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank"); }
                    catch { toast.error("PDF export failed"); }
                  }} className="re-chip h-10 px-4 text-xs font-bold inline-flex items-center gap-1.5" data-testid="rm-stmt-pdf"><Download className="h-3.5 w-3.5" /> PDF</button>
                </>}
              </div>
              {stmt && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="re-card p-4"><p className="text-[10px] font-extrabold uppercase tracking-wider opacity-50">Client ID</p><p className="re-h font-extrabold text-lg mt-1 font-mono">{stmt.summary.client_id || "—"}</p></div>
                    {[["Investment Amount", stmt.summary.investment_amount], ["Total Paid (Verified)", stmt.summary.total_verified],
                      ["Remaining Balance", stmt.summary.outstanding], [`Paid (${stmt.summary.period})`, stmt.summary.period_verified],
                      ["Current Month Payments", stmt.summary.month_payments]].map(([l, v]) => (
                      <div key={l} className="re-card p-4">
                        <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-50">{l}</p>
                        <p className="re-h font-extrabold text-lg mt-1" style={l === "Remaining Balance" && v > 0 ? { color: "#d97706" } : undefined}>{inr2(v)}</p>
                      </div>
                    ))}
                    <div className="re-card p-4"><p className="text-[10px] font-extrabold uppercase tracking-wider opacity-50">Last Payment Date</p><p className="re-h font-extrabold text-lg mt-1">{stmt.summary.last_payment_date || "—"}</p></div>
                    <div className="re-card p-4"><p className="text-[10px] font-extrabold uppercase tracking-wider opacity-50">Opening Balance</p><p className="re-h font-extrabold text-lg mt-1">{inr2(stmt.summary.opening_balance)}</p></div>
                  </div>
                  <div className="re-card overflow-x-auto">
                    <table className="w-full text-sm min-w-[860px]" data-testid="rm-stmt-table">
                      <thead><tr className="text-left text-[10px] uppercase tracking-wider opacity-50 border-b" style={{ borderColor: "color-mix(in srgb, var(--re-t) 10%, transparent)" }}>
                        {["S.No", "Payment Date", "Payment Time", "Description", "Payment Method", "Recorded By", "Amount Paid", "Status", "Remarks"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {stmt.rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm opacity-50">No transactions in this period.</td></tr>}
                        {stmt.rows.map((p, i) => (
                          <tr key={p.id} className="border-b last:border-0" style={{ borderColor: "color-mix(in srgb, var(--re-t) 8%, transparent)" }} data-testid={`rm-stmt-row-${p.id}`}>
                            <td className="px-4 py-3">{i + 1}</td>
                            <td className="px-4 py-3 text-xs font-bold">{p.payment_date}</td>
                            <td className="px-4 py-3 text-xs">{p.payment_time}</td>
                            <td className="px-4 py-3 text-xs max-w-[150px] truncate">{p.description || "—"}</td>
                            <td className="px-4 py-3 text-xs font-semibold">{p.method}</td>
                            <td className="px-4 py-3 text-xs opacity-70">{p.recorded_by?.name || "—"}</td>
                            <td className="px-4 py-3 font-bold">{inr2(p.amount)}</td>
                            <td className="px-4 py-3">{stBadge(p.status)}</td>
                            <td className="px-4 py-3 text-xs max-w-[140px] truncate">{p.remarks || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "notifications" && (
            <div className="space-y-4" data-testid="rm-notifications">
              <div className="flex items-center justify-between">
                <h2 className="re-h font-extrabold text-xl">Notifications</h2>
                {notifs?.unread > 0 && (
                  <button className="re-chip h-9 px-4 text-xs font-bold" data-testid="rm-notif-read"
                    onClick={() => act(() => api.post("/re/org/notifications/read", {}, { headers: H }), "All marked as read")}>Mark all read</button>
                )}
              </div>
              {!notifs ? <Loader2 className="h-6 w-6 animate-spin" /> : notifs.notifications.length === 0 ? <p className="text-sm opacity-50 py-8 text-center">No notifications yet.</p> : (
                <div className="space-y-2.5">
                  {notifs.notifications.map((n) => (
                    <div key={n.id} className="re-card p-4" style={!n.read ? { borderLeft: "3px solid var(--re-p)" } : undefined} data-testid={`rm-notif-${n.id}`}>
                      <p className="font-bold text-sm">{n.title}</p>
                      <p className="text-xs opacity-60 mt-1">{n.body}</p>
                      <p className="text-[10px] opacity-40 mt-1.5">{String(n.created_at).slice(0, 16).replace("T", " ")}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "security" && <SecurityTab H={H} act={act} busy={busy} />}

          {tab === "favourites" && <FavouritesTab slug={slug} H={H} />}
        </div>
      </div>

      {/* ADD CLIENT DIALOG */}
      {dlg?.mode === "addClient" && (
        <Modal onClose={() => setDlg(null)} title="Add Client" testid="rm-addclient-dialog">
          <div className="grid sm:grid-cols-2 gap-3">
            {[["full_name", "Full Name *"], ["email", "Email *"], ["password", "Password * (min 6)"], ["mobile", "Mobile Number"],
              ["district", "District"], ["investment_amount", "Investment Amount (₹)"]].map(([k, l]) => (
              <div key={k}>
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">{l}</p>
                <input value={dlg.form[k]} type={k === "password" ? "password" : k === "investment_amount" ? "number" : "text"} data-testid={`rac-${k}`}
                  onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, [k]: e.target.value } })}
                  className="w-full h-10 px-3 rounded-lg text-sm font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
              </div>
            ))}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">Joining Date</p>
              <input type="date" value={dlg.form.joining_date} data-testid="rac-joining_date"
                onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, joining_date: e.target.value } })}
                className="w-full h-10 px-3 rounded-lg text-sm font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
            </div>
          </div>
          <button className="re-btn w-full h-11 text-sm mt-4" disabled={busy} data-testid="rac-save"
            onClick={() => act(async () => { await api.post("/re/org/clients", dlg.form, { headers: H }); setDlg(null); }, "Client added")}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Create Client"}
          </button>
        </Modal>
      )}

      {/* RECORD PAYMENT DIALOG */}
      {dlg?.mode === "payment" && (
        <Modal onClose={() => setDlg(null)} title={`Record Payment — ${dlg.name}`} testid="rm-payment-dialog">
          <div className="grid sm:grid-cols-2 gap-3">
            {[["amount", "Amount (₹) *", "number"], ["payment_date", "Payment Date", "date"], ["payment_time", "Payment Time", "time"], ["txn_ref", "Transaction Ref No.", "text"]].map(([k, l, t]) => (
              <div key={k}>
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">{l}</p>
                <input type={t} value={dlg.form[k]} data-testid={`rp-${k}`}
                  onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, [k]: e.target.value } })}
                  className="w-full h-10 px-3 rounded-lg text-sm font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
              </div>
            ))}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">Method</p>
              <select value={dlg.form.method} data-testid="rp-method"
                onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, method: e.target.value } })}
                className="w-full h-10 px-3 rounded-lg text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">Direction</p>
              <select value={dlg.form.direction} data-testid="rp-direction"
                onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, direction: e.target.value } })}
                className="w-full h-10 px-3 rounded-lg text-xs font-bold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }}>
                <option value="received">Received</option><option value="sent">Sent</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">Remarks</p>
            <textarea rows={2} value={dlg.form.remarks} data-testid="rp-remarks"
              onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, remarks: e.target.value } })}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
          </div>
          <p className="text-[11px] opacity-50 mt-2">Payment will be <b>PENDING</b> until the administrator verifies it.</p>
          <button className="re-btn w-full h-11 text-sm mt-3" disabled={busy} data-testid="rp-save"
            onClick={() => act(async () => { await api.post("/re/org/payments", { ...dlg.form, member_id: dlg.member_id }, { headers: H }); setDlg(null); }, "Payment recorded (pending verification)")}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Save Payment"}
          </button>
        </Modal>
      )}

      {/* CLIENT DETAIL DIALOG */}
      {dlg?.mode === "client" && (
        <Modal onClose={() => setDlg(null)} title="Client Details" testid="rm-client-dialog" wide>
          <ClientDetail id={dlg.id} H={H} canEdit={can("clients_edit")} canStmt={can("statements")} onDone={() => { setDlg(null); loadClients(); }} siteName={data.website?.name} />
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative re-card w-full ${wide ? "max-w-3xl" : "max-w-lg"} p-6 max-h-[85vh] overflow-y-auto`} style={{ background: "var(--re-bg)", color: "var(--re-t)" }}>
        <div className="flex items-center justify-between mb-4">
          <p className="re-h font-extrabold text-lg">{title}</p>
          <button onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--re-t) 8%, transparent)" }} data-testid={`${testid}-close`}><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SecurityTab({ H, act, busy }) {
  const [f, setF] = useState({ current: "", password: "", confirm: "" });
  return (
    <div className="re-card p-6 sm:p-8 max-w-md" data-testid="rm-security">
      <h2 className="re-h font-extrabold text-xl mb-1">Security Settings</h2>
      <p className="text-[11px] opacity-50 mb-6">Change your account password.</p>
      <div className="space-y-3.5">
        {[["current", "Current Password"], ["password", "New Password (min 6)"], ["confirm", "Confirm New Password"]].map(([k, l]) => (
          <div key={k}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">{l}</p>
            <input type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`rm-pw-${k}`}
              className="w-full h-11 px-4 rounded-xl text-sm font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
          </div>
        ))}
        <button className="re-btn w-full h-11 text-sm" disabled={busy} data-testid="rm-pw-save"
          onClick={() => {
            if (f.password !== f.confirm) { toast.error("Passwords do not match"); return; }
            act(async () => { await api.post("/re/org/me/change-password", { current: f.current, password: f.password }, { headers: H }); setF({ current: "", password: "", confirm: "" }); }, "Password changed");
          }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Change Password"}
        </button>
      </div>
    </div>
  );
}

function FavouritesTab({ slug, H }) {
  const [fav, setFav] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get(`/re/public/${slug}/my/enquiries`, { headers: H }).then((r) => r.data.enquiries || []).catch(() => []),
      api.get(`/re/public/${slug}/my/saved`, { headers: H }).then((r) => r.data.properties || []).catch(() => []),
      api.get(`/re/public/${slug}/my/invoices`, { headers: H }).then((r) => r.data.invoices || []).catch(() => []),
    ]).then(([enquiries, saved, invoices]) => setFav({ enquiries, saved, invoices }));
  }, []);
  const openPdf = async (inv) => {
    try {
      const r = await api.get(`/re/public/${slug}/my/invoices/${inv.id}/pdf`, { headers: H, responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("Could not download invoice"); }
  };
  if (!fav) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-6" data-testid="rm-favourites">
      <div>
        <h2 className="re-h font-extrabold text-xl mb-3">My Enquiries</h2>
        {fav.enquiries.length === 0 && <p className="text-sm opacity-50">No enquiries yet.</p>}
        <div className="space-y-2.5">
          {fav.enquiries.map((e) => (
            <div key={e.id} className="re-card p-4 flex flex-wrap justify-between gap-3" data-testid={`rm-enq-${e.id}`}>
              <div><p className="font-bold text-sm">{e.property_name || "General enquiry"}</p><p className="text-xs opacity-60 mt-0.5">{e.message || e.type}</p></div>
              <span className="re-chip px-3 py-1 text-[10px] uppercase font-extrabold h-fit">{e.status}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="re-h font-extrabold text-xl mb-3">Saved Properties</h2>
        {fav.saved.length === 0 && <p className="text-sm opacity-50">No saved properties yet — tap the heart on any property.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {fav.saved.map((p) => (
            <div key={p.id} className="re-card p-4" data-testid={`rm-saved-${p.id}`}>
              <p className="font-bold text-sm line-clamp-1">{p.title}</p>
              <p className="text-xs opacity-50">{(p.location || {}).district || ""}</p>
              <p className="font-extrabold mt-1" style={{ color: "var(--re-p)" }}>{inr2(p.price)}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="re-h font-extrabold text-xl mb-3">Invoices</h2>
        {fav.invoices.length === 0 && <p className="text-sm opacity-50">No invoices yet.</p>}
        <div className="space-y-2.5">
          {fav.invoices.map((i) => (
            <div key={i.id} className="re-card p-4 flex flex-wrap items-center gap-4" data-testid={`rm-inv-${i.id}`}>
              <div className="flex-1 min-w-[160px]"><p className="font-bold text-sm">{i.number}</p><p className="text-xs opacity-50">{i.invoice_date}{i.period ? ` · ${i.period}` : ""}</p></div>
              <p className="font-extrabold">{inr2(i.total)}</p>
              <button onClick={() => openPdf(i)} className="re-chip h-9 px-4 text-xs font-bold inline-flex items-center gap-1.5" data-testid={`rm-inv-pdf-${i.id}`}><Download className="h-3.5 w-3.5" /> PDF</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ClientDetail({ id, H, canEdit, canStmt, onDone, siteName }) {
  const [d, setD] = useState(null);
  const [sub, setSub] = useState("profile");
  const [stmt, setStmt] = useState(null);
  const [ef, setEf] = useState(null);
  const load = () => api.get(`/re/org/clients/${id}`, { headers: H }).then((r) => { setD(r.data.member); setEf(null); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (sub === "statement" && canStmt) {
      const n = new Date();
      api.get(`/re/org/clients/${id}/statement?month=${n.getMonth() + 1}&year=${n.getFullYear()}`, { headers: H }).then((r) => setStmt(r.data)).catch(() => setStmt(null));
    }
  }, [sub]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const Row = ({ l, v, mono }) => <div><p className="text-[10px] font-bold uppercase tracking-wider opacity-45">{l}</p><p className={`font-bold text-sm mt-1 ${mono ? "font-mono" : ""}`}>{v || "—"}</p></div>;
  const SUBS = [["profile", "Profile"], ["account", "Account"], ...(canStmt ? [["statement", "Statement"]] : [])];
  return (
    <div data-testid="rm-client-detail">
      <div className="flex gap-2 mb-5">
        {SUBS.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`rm-cd-${k}`}
            className="px-4 py-2 rounded-full text-xs font-bold"
            style={sub === k ? { background: "var(--re-p)", color: "#fff" } : { border: "1px solid color-mix(in srgb, var(--re-t) 15%, transparent)" }}>{l}</button>
        ))}
      </div>
      {sub === "profile" && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            {PROFILE_ROWS.filter(([k]) => k !== "investment_amount" || true).map(([k, l]) => (
              <div key={k}>
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1">{l}</p>
                {ef && ["full_name", "mobile", "whatsapp", "district", "joining_date", "closing_date", "investment_amount"].includes(k) ? (
                  <input value={ef[k] ?? ""} type={k.includes("date") ? "date" : k === "investment_amount" ? "number" : "text"}
                    onChange={(e) => setEf({ ...ef, [k]: e.target.value })} data-testid={`rm-cd-edit-${k}`}
                    className="w-full h-9 px-2.5 rounded-lg text-xs font-semibold bg-transparent border outline-none" style={{ borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" }} />
                ) : <p className="font-bold text-sm">{k === "investment_amount" ? inr2(d[k]) : d[k] || "—"}</p>}
              </div>
            ))}
            <Row l="Verified Paid" v={inr2(d.verified_paid)} /><Row l="Balance" v={inr2(d.balance)} />
          </div>
          {d.address && <Row l="Address" v={d.address} />}
          {canEdit && (
            <div className="flex gap-2">
              {!ef ? (
                <button className="re-btn h-10 px-5 text-xs" data-testid="rm-cd-editbtn" onClick={() => setEf({ full_name: d.full_name, mobile: d.mobile, whatsapp: d.whatsapp, district: d.district, joining_date: d.joining_date, closing_date: d.closing_date, investment_amount: d.investment_amount })}>Edit Details</button>
              ) : (
                <>
                  <button className="re-btn h-10 px-5 text-xs" data-testid="rm-cd-savebtn"
                    onClick={async () => { try { await api.patch(`/re/org/clients/${id}`, ef, { headers: H }); toast.success("Client updated"); load(); } catch (e) { toast.error(errMsg(e)); } }}>Save</button>
                  <button className="re-chip h-10 px-5 text-xs font-bold" onClick={() => setEf(null)}>Cancel</button>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {sub === "account" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
          <Row l="Account Holder" v={d.bank?.holder} /><Row l="Account Number" v={d.bank?.account_number} mono />
          <Row l="Bank" v={d.bank?.bank_name} /><Row l="Branch" v={d.bank?.branch} /><Row l="IFSC" v={d.bank?.ifsc} mono />
          <Row l="MICR" v={d.bank?.micr} mono /><Row l="Aadhaar" v={d.aadhaar} mono /><Row l="Client ID" v={d.client_id} mono /><Row l="Referral ID" v={d.referral_id} mono />
        </div>
      )}
      {sub === "statement" && (
        <div>
          {!stmt ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {[["Investment", stmt.summary.investment_amount], [`Paid (${stmt.summary.period})`, stmt.summary.period_verified],
                  ["Total Verified", stmt.summary.total_verified], ["Outstanding", stmt.summary.outstanding]].map(([l, v]) => (
                  <div key={l} className="re-card p-3.5"><p className="text-[9px] font-extrabold uppercase tracking-wider opacity-50">{l}</p><p className="font-extrabold text-sm mt-1">{inr2(v)}</p></div>
                ))}
              </div>
              <button className="re-chip h-9 px-4 text-xs font-bold inline-flex items-center gap-1.5 mb-3" data-testid="rm-cd-print"
                onClick={() => printStatement(siteName || "Real Estate", stmt.summary, stmt.rows)}><Printer className="h-3.5 w-3.5" /> Print Statement</button>
              <div className="space-y-2">
                {stmt.rows.length === 0 && <p className="text-sm opacity-50">No transactions this month.</p>}
                {stmt.rows.map((p) => (
                  <div key={p.id} className="re-card p-3.5 flex items-center gap-3 text-xs" data-testid={`rm-cd-pay-${p.id}`}>
                    <span className="font-bold">{p.payment_date}</span><span className="opacity-60">{p.method}</span>
                    <span className="font-extrabold ml-auto">{inr2(p.amount)}</span>
                    <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full text-white" style={{ background: STATUS_STYLE[p.status] }}>{p.status}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileSections({ me, H, reload }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const m = me.member;
  const editable = new Set(me.editable_fields || []);
  const pending = new Set(m.pending_fields || []);
  const inp = "w-full h-10 px-3 rounded-lg text-sm font-semibold bg-transparent border outline-none";
  const bc = { borderColor: "color-mix(in srgb, var(--re-t) 18%, transparent)" };

  const start = () => {
    setDraft({ full_name: m.full_name || "", email: m.email || "", mobile: m.mobile || "", whatsapp: m.whatsapp || "",
      joining_date: m.joining_date || "", address: m.address || "", district: m.district || "", state: m.state || "", pincode: m.pincode || "",
      aadhaar: "", pan: "",
      bank: { holder: m.bank?.holder || "", account_number: "", bank_name: m.bank?.bank_name || "", branch: m.bank?.branch || "", ifsc: m.bank?.ifsc || "", micr: m.bank?.micr || "" } });
    setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of editable) {
        if (k === "bank" || k === "photo_url") continue;
        if (k === "aadhaar" || k === "pan") { if ((draft[k] || "").trim()) payload[k] = draft[k]; continue; }
        if (k in draft) payload[k] = draft[k];
      }
      if (editable.has("bank")) {
        const b = { ...draft.bank };
        if (!(b.account_number || "").trim()) delete b.account_number;
        payload.bank = b;
      }
      const r = await api.patch("/re/org/me", payload, { headers: H });
      if (r.data.pending?.length) toast.success(`Saved — sent for admin approval: ${r.data.pending.join(", ")}`);
      else toast.success("Details saved");
      setEditing(false); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const PendBadge = () => <span className="text-[8px] font-extrabold px-1.5 py-0.5 rounded-full text-white ml-1.5" style={{ background: "#d97706" }}>AWAITING APPROVAL</span>;
  const Row = ({ k, l, type }) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1 flex items-center">{l}{pending.has(k) && <PendBadge />}</p>
      {editing && editable.has(k) ? (
        k === "address"
          ? <textarea rows={2} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className={`${inp} h-auto py-2`} style={bc} data-testid={`rm-edit-${k}`} />
          : <input type={type || (k.includes("date") ? "date" : "text")} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
              placeholder={(k === "aadhaar" || k === "pan") && m[k] ? `Current: ${m[k]} — type to replace` : ""} className={inp} style={bc} data-testid={`rm-edit-${k}`} />
      ) : <p className="font-bold text-sm" data-testid={`rm-view-${k}`}>{k === "investment_amount" ? inr2(m[k]) : m[k] || "—"}</p>}
    </div>
  );
  const BankRow = ({ k, l }) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-45 mb-1 flex items-center">{l}{pending.has("bank") && <PendBadge />}</p>
      {editing && editable.has("bank") ? (
        <input value={draft.bank[k]} onChange={(e) => setDraft({ ...draft, bank: { ...draft.bank, [k]: e.target.value } })}
          placeholder={k === "account_number" && m.bank?.account_number ? `Current: ${m.bank.account_number} — type to replace` : ""}
          className={inp} style={bc} data-testid={`rm-edit-bank-${k}`} />
      ) : <p className="font-bold text-sm font-mono" data-testid={`rm-view-bank-${k}`}>{m.bank?.[k] || "—"}</p>}
    </div>
  );
  const Section = ({ title, icon: I, children, testid }) => (
    <div className="re-card p-6" data-testid={testid}>
      <p className="font-extrabold text-sm mb-4 flex items-center gap-2"><span className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--re-p) 12%, transparent)", color: "var(--re-p)" }}><I className="h-3.5 w-3.5" /></span>{title}</p>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">{children}</div>
    </div>
  );

  return (
    <div className="space-y-5 max-w-3xl" data-testid="rm-profile">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="re-h font-extrabold text-xl">My Profile</h2>
          <p className="text-[11px] opacity-50 mt-0.5">Enter and update your own details. Protected fields (Client ID, Referral ID) are admin-managed.</p>
        </div>
        <div className="flex gap-2">
          <label className="re-chip h-9 px-4 text-xs font-bold inline-flex items-center gap-1.5 cursor-pointer" data-testid="rm-photo-upload">
            <Camera className="h-3.5 w-3.5" /> Photo
            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              try { await api.post("/re/org/me/photo", fd, { headers: { ...H, "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); reload(); }
              catch (e2) { toast.error(errMsg(e2)); }
            }} />
          </label>
          {!editing ? (
            <button className="re-btn h-9 px-5 text-xs" onClick={start} data-testid="rm-profile-edit">Edit Profile</button>
          ) : (
            <>
              <button className="re-btn h-9 px-5 text-xs" onClick={save} disabled={busy} data-testid="rm-profile-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Details"}</button>
              <button className="re-chip h-9 px-4 text-xs font-bold" onClick={() => setEditing(false)} data-testid="rm-profile-cancel">Cancel</button>
            </>
          )}
        </div>
      </div>
      {pending.size > 0 && (
        <p className="text-xs font-bold px-4 py-3 rounded-xl" style={{ background: "rgba(217,119,6,.08)", color: "#d97706" }} data-testid="rm-pending-note">
          Changes awaiting admin approval: {[...pending].join(", ").replace(/_/g, " ")}
        </p>
      )}
      <Section title="Personal Details" icon={User} testid="rm-sec-personal">
        <Row k="full_name" l="Full Name" /><Row k="email" l="Email Address" /><Row k="mobile" l="Mobile Number" /><Row k="whatsapp" l="WhatsApp Number" />
        <Row k="joining_date" l="Joining Date" /><Row k="closing_date" l="Closing Date" /><Row k="rank" l="Rank" /><Row k="investment_amount" l="Investment Amount" />
        <Row k="client_id" l="Client ID" /><Row k="referral_id" l="Referral ID" />
      </Section>
      <Section title="Identity Details" icon={ShieldCheck} testid="rm-sec-identity">
        <Row k="aadhaar" l="Aadhaar Number (12 digits)" /><Row k="pan" l="PAN Number" />
      </Section>
      <Section title="Bank Account Details" icon={Landmark} testid="rm-sec-bank">
        <BankRow k="holder" l="Account Holder Name" /><BankRow k="account_number" l="Account Number" />
        <BankRow k="bank_name" l="Bank Name" /><BankRow k="branch" l="Branch Name" /><BankRow k="ifsc" l="IFSC Code" /><BankRow k="micr" l="MICR Code" />
      </Section>
      <Section title="Address Details" icon={FileText} testid="rm-sec-address">
        <div className="sm:col-span-2"><Row k="address" l="Address" /></div>
        <Row k="district" l="District" /><Row k="state" l="State" /><Row k="pincode" l="PIN Code" />
      </Section>
      {editing && (
        <button className="re-btn h-11 px-8 text-sm" onClick={save} disabled={busy} data-testid="rm-profile-save-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Details"}
        </button>
      )}
    </div>
  );
}
